import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { ChannelBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  CalendarCheck,
  Building2,
  Calendar,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  User,
  Bot,
  RefreshCw,
  Inbox,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react';

export const BookingsPage = () => {
  const api = useApi();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [messages, setMessages] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(null);
  const [syncingMessages, setSyncingMessages] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchBookings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '50');
      if (search) params.set('search', search);
      if (channelFilter) params.set('channel', channelFilter);
      
      const response = await api.get(`/bookings?${params.toString()}`);
      const data = response.data;
      setBookings(data.bookings || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, page, search, channelFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBookings();
  };

  const handleSyncMessages = async () => {
    setSyncingMessages(true);
    try {
      const response = await api.post('/beds24/sync-messages');
      toast.success(response.data.message || 'Messages synchronisés');
      if (expandedBooking) loadMessages(expandedBooking);
    } catch (error) {
      toast.error('Erreur de synchronisation des messages');
    } finally {
      setSyncingMessages(false);
    }
  };

  const loadMessages = async (bookingId) => {
    setLoadingMessages(bookingId);
    try {
      const response = await api.get(`/bookings/${bookingId}/messages`);
      setMessages(prev => ({ ...prev, [bookingId]: response.data }));
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoadingMessages(null);
    }
  };

  const toggleBooking = (bookingId) => {
    if (expandedBooking === bookingId) {
      setExpandedBooking(null);
    } else {
      setExpandedBooking(bookingId);
      if (!messages[bookingId]) loadMessages(bookingId);
    }
  };

  const getSenderIcon = (sender) => {
    switch (sender) {
      case 'guest': return <User size={14} className="text-blue-500" />;
      case 'host': return <Bot size={14} className="text-emerald-500" />;
      default: return <MessageSquare size={14} className="text-slate-400" />;
    }
  };

  const getSenderLabel = (sender) => {
    const labels = { guest: 'Voyageur', host: 'Hôte', internal: 'Note interne', system: 'Système' };
    return labels[sender] || sender;
  };

  return (
    <MainLayout>
      <Header
        title="Réservations"
        subtitle={`${total} réservations synchronisées depuis Beds24`}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="p-6 space-y-4 animate-fadeIn" data-testid="bookings-page">
        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher un voyageur..."
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" size="sm" data-testid="search-btn">
              Rechercher
            </Button>
          </form>

          <div className="flex gap-2 items-center">
            <Filter size={14} className="text-slate-400" />
            {['', 'airbnb', 'booking.com', 'direct'].map(ch => (
              <Button
                key={ch || 'all'}
                variant={channelFilter === ch ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setChannelFilter(ch); setPage(1); }}
                data-testid={`filter-${ch || 'all'}`}
                className={channelFilter === ch ? 'bg-[#FF5A6E] hover:bg-[#e5485c]' : ''}
              >
                {ch === '' ? 'Tous' : ch === 'airbnb' ? 'Airbnb' : ch === 'booking.com' ? 'Booking' : 'Direct'}
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncMessages}
            disabled={syncingMessages}
            data-testid="sync-messages-btn"
          >
            <RefreshCw size={14} className={`mr-2 ${syncingMessages ? 'animate-spin' : ''}`} />
            {syncingMessages ? 'Sync...' : 'Sync messages'}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : bookings.length === 0 ? (
          <Card className="bg-white border-slate-100">
            <CardContent className="py-12 text-center">
              <CalendarCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune réservation trouvée</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-2">
              {bookings.map((booking) => {
                const isExpanded = expandedBooking === booking.id;
                const bookingMessages = messages[booking.id] || [];
                const isLoadingMsgs = loadingMessages === booking.id;

                return (
                  <Card
                    key={booking.id}
                    className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <CardContent className="p-0">
                      <div
                        className="px-4 py-3 cursor-pointer hover:bg-slate-50/50 transition-colors"
                        onClick={() => toggleBooking(booking.id)}
                        data-testid={`booking-toggle-${booking.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-semibold text-slate-900 text-sm">{booking.guest_name}</span>
                              <ChannelBadge channel={booking.channel} />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Building2 size={12} />
                                {booking.property_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {booking.check_in} &rarr; {booking.check_out}
                              </span>
                            </div>
                          </div>
                          {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-100 p-4 bg-slate-50/50" data-testid={`booking-messages-${booking.id}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <MessageSquare size={16} className="text-[#FF5A6E]" />
                            <h4 className="text-sm font-semibold text-slate-700">Conversation Beds24</h4>
                            {bookingMessages.length > 0 && (
                              <span className="text-xs text-slate-400">({bookingMessages.length} message{bookingMessages.length > 1 ? 's' : ''})</span>
                            )}
                          </div>

                          {isLoadingMsgs ? (
                            <div className="text-center py-4 text-sm text-slate-400">Chargement...</div>
                          ) : bookingMessages.length === 0 ? (
                            <div className="text-center py-4">
                              <Inbox size={20} className="text-slate-300 mx-auto mb-1" />
                              <p className="text-sm text-slate-400">Aucun message pour cette réservation</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-[350px] overflow-y-auto">
                              {bookingMessages.map((msg, idx) => (
                                <div
                                  key={msg.beds24_message_id || idx}
                                  className={`flex gap-2 p-2 rounded-lg text-sm ${
                                    msg.sender === 'host' ? 'bg-emerald-50 ml-6' :
                                    msg.sender === 'guest' ? 'bg-white border border-slate-200 mr-6' :
                                    'bg-amber-50 mx-3'
                                  }`}
                                  data-testid={`message-${idx}`}
                                >
                                  <div className="mt-0.5">{getSenderIcon(msg.sender)}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className="text-xs font-medium text-slate-600">{getSenderLabel(msg.sender)}</span>
                                      <span className="text-xs text-slate-300">
                                        {msg.sent_at ? new Date(msg.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                      </span>
                                    </div>
                                    <p className="text-slate-700 whitespace-pre-wrap break-words">{msg.content}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2" data-testid="pagination">
                <span className="text-sm text-slate-500">
                  Page {page} / {totalPages} ({total} réservations)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft size={14} className="mr-1" /> Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="next-page-btn"
                  >
                    Suivant <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BookingsPage;
